import React, { useState, useEffect, useCallback } from 'react';
import { Download } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { styles } from '../theme';
import { API_URL, fetchWithAuth } from '../api';
import { exportReportToPDF } from '../utils/reportPdf';
import { formatCurrency } from '../utils/formatUtils';
import { getPHTDate } from '../utils/dateUtils';

const fmt = (v) => formatCurrency(v);
const defaultDates = () => {
  const to = getPHTDate();
  const from = getPHTDate();
  from.setDate(from.getDate() - 7);
  const s = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { from: s(from), to: s(to) };
};

const PosSalesByPaymentTypeReport = ({ token }) => {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [branchId, setBranchId] = useState('');
  const [branches, setBranches] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const { from, to } = defaultDates();
    setDateFrom(from);
    setDateTo(to);
    fetchWithAuth(`${API_URL}/branchesdropdown`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json()).then(setBranches).catch(err => console.error(err));
  }, [token]);

  const generate = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, branch_id: branchId });
      const res = await fetchWithAuth(`${API_URL}/pos/reports/sales-by-payment-type?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      setRows(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, dateFrom, dateTo, branchId]);

  useEffect(() => { if (dateFrom && dateTo) generate(); }, [dateFrom, dateTo, generate]);

  const totalNet = rows.reduce((sum, r) => sum + Number(r.grand_total || 0), 0);

  const exportPdf = () => {
    exportReportToPDF({
      title: 'POS Sales by Payment Type Report',
      subtitle: `${dateFrom} to ${dateTo}`,
      columns: [
        { header: 'Payment Method', key: 'payment_method_name' },
        { header: 'Txns', key: 'transaction_count', align: 'right' },
        { header: 'Subtotal', key: 'subtotal', align: 'right' },
        { header: 'Discount', key: 'discount_total', align: 'right' },
        { header: 'Net Sales', key: 'grand_total', align: 'right' }
      ],
      rows: rows.map(r => ({ ...r, subtotal: fmt(r.subtotal), discount_total: fmt(r.discount_total), grand_total: fmt(r.grand_total) })),
      filename: `pos_sales_by_payment_type_${dateFrom}_to_${dateTo}.pdf`
    });
  };

  return (
    <div>
      <h1 style={styles.title}>Sales by Payment Type Report</h1>
      <div style={{ ...styles.card, marginTop: '16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '16px' }}>
          <div style={styles.formGroup}><label style={styles.label}>From</label><input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={styles.input} /></div>
          <div style={styles.formGroup}><label style={styles.label}>To</label><input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={styles.input} /></div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Branch</label>
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)} style={styles.input}>
              <option value="">All Branches</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={generate} disabled={loading} style={{ ...styles.button, ...styles.buttonReport }}>
            <Download size={18} /> {loading ? 'Generating...' : 'Generate Report'}
          </button>
          {rows.length > 0 && <button onClick={exportPdf} style={{ ...styles.button, ...styles.buttonSecondary }}>Export PDF</button>}
        </div>
      </div>

      {rows.length > 0 && (
        <div style={{ ...styles.card, marginTop: '16px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Net Sales Share by Payment Method</h3>
          <div style={{ height: '300px', width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={rows.map(r => ({ ...r, grand_total: Number(r.grand_total || 0) }))}
                  dataKey="grand_total"
                  nameKey="payment_method_name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={(d) => d.payment_method_name}
                >
                  {rows.map((r, i) => <Cell key={i} fill={r.payment_method_color || '#9ca3af'} />)}
                </Pie>
                <Tooltip formatter={(v) => `₱${fmt(v)}`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div style={{ ...styles.card, marginTop: '16px', padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Payment Method</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Txns</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Subtotal</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Discount</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Net Sales</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td style={styles.td}>
                    <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: r.payment_method_color, marginRight: '8px' }} />
                    {r.payment_method_name || 'Unknown'}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{r.transaction_count}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>₱{fmt(r.subtotal)}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>₱{fmt(r.discount_total)}</td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>₱{fmt(r.grand_total)}</td>
                </tr>
              ))}
              {rows.length === 0 && !loading && (
                <tr><td colSpan={5} style={{ ...styles.td, textAlign: 'center', color: '#9ca3af' }}>No sales data for this period.</td></tr>
              )}
              {rows.length > 0 && (
                <tr style={{ backgroundColor: '#ecfeff' }}>
                  <td style={{ ...styles.td, fontWeight: 700 }} colSpan={4}>TOTAL NET SALES</td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>₱{fmt(totalNet)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PosSalesByPaymentTypeReport;
